import React, { useState, useEffect } from "react";
import AdminSidebar from "../../components/AdminSidebar";
import {
  Database,
  Search,
  Trash2,
  Eye,
  Edit,
  Loader,
  FileText,
  Menu,
} from "lucide-react";
import {
  fetchCustomModels,
  updateModelStatus,
  deleteCustomModel,
  fetchModels,
  deleteIndex,
  getPromptTemplates,
} from "../../../services/modelApi";
import { toast } from "react-toastify";
import { ConfirmationModal } from "../../components/ConfirmationModel";
import { useNavigate } from "react-router-dom";
import { Drawer } from "../../components/ui/Drawer";

const KnowledgeBaseListing = () => {
  const [models, setModels] = useState([]);
  const [indexes, setIndexes] = useState([]);
  const [promptTemplates, setPromptTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [updatingModel, setUpdatingModel] = useState(null);
  const [activeTab, setActiveTab] = useState("knowledgebase");
  const navigate = useNavigate();

  // Modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modelToDelete, setModelToDelete] = useState(null);

  useEffect(() => {
    fetchKnowledgeBases();
  }, []);

  const fetchKnowledgeBases = async () => {
    setLoading(true);
    try {
      const response = await fetchCustomModels();
      const res = await fetchModels();

      // Separate knowledge bases and prompt templates based on naming convention
      const allIndexes = res.data.map((item, i) => ({
        name: item.name,
        usedIn: item.usedIn,
        status: item.status,
      }));

      // Knowledge bases are those that don't end with '-prompts'
      const knowledgeBases = allIndexes.filter(
        (item) => !item.name.endsWith("-prompts")
      );

      // Prompt templates are those that end with '-prompts'
      const promptTemplateIndexes = allIndexes.filter((item) =>
        item.name.endsWith("-prompts")
      );

      setIndexes(knowledgeBases);
      setPromptTemplates(
        promptTemplateIndexes.map((item) => ({
          id: item.name,
          name: item.name.replace("-prompts", ""), // Remove -prompts suffix for display
          type: "template",
          knowledgeBase: item.name.replace("-prompts", ""),
          status: item.status.toLowerCase(),
          usedIn: item.usedIn,
          createdAt: new Date().toISOString(), // Placeholder since we don't have this data
        }))
      );

      setModels(response.data);
    } catch (error) {
      console.error("Error fetching models:", error);
      toast.error("Failed to fetch knowledge bases");
    } finally {
      setLoading(false);
    }
  };

  // Open delete confirmation modal
  const handleDeleteClick = (model) => {
    setModelToDelete(model);
    setShowDeleteModal(true);
  };

  // Handle confirmed deletion
  const handleConfirmDelete = async () => {
    if (!modelToDelete) return;

    setUpdatingModel(modelToDelete.name);

    try {
      setDeleteLoading((p) => [...p, modelToDelete.name]);
      await deleteIndex(modelToDelete.name);
      setIndexes((prevModels) =>
        prevModels.filter((model) => model.name !== modelToDelete.name)
      );
      toast.success("Bot deleted successfully");
    } catch (error) {
      console.error("Error deleting model:", error);
      toast.error(error.message || "Failed to delete bot");
    } finally {
      setDeleteLoading((p) => p.filter((x) => x !== modelToDelete.name));
      setUpdatingModel(null);
      setModelToDelete(null);
      setShowDeleteModal(false);
    }
  };

  // Close modal
  const handleCloseModal = () => {
    setShowDeleteModal(false);
    setModelToDelete(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "bg-green-500/20 text-green-400";
      case "inactive":
        return "bg-gray-500/20 text-gray-400";
      default:
        return "bg-gray-500/20 text-gray-400";
    }
  };

  const getStatusDot = (status) => {
    switch (status) {
      case "active":
        return activeTab === "knowledgebase" ? "bg-green-400" : "bg-purple-400";
      case "inactive":
        return "bg-gray-500";
      default:
        return "bg-gray-500";
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "system":
        return "bg-blue-500/20 text-blue-400";
      case "user":
        return "bg-green-500/20 text-green-400";
      case "context":
        return "bg-orange-500/20 text-orange-400";
      default:
        return "bg-gray-500/20 text-gray-400";
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const filteredModels =
    indexes.filter((model) =>
      model.name?.toLowerCase().includes(searchTerm.toLowerCase())
    ) || indexes;

  const filteredPromptTemplates =
    promptTemplates.filter(
      (template) =>
        template.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.knowledgeBase?.toLowerCase().includes(searchTerm.toLowerCase())
    ) || promptTemplates;

  const currentData =
    activeTab === "knowledgebase" ? filteredModels : filteredPromptTemplates;

  const [showSidebar, setShowSidebar] = useState(false);
  const onClose = () => {
    setShowSidebar(false);
  };

  return (
    <div className="w-screen min-h-screen bg-gray-800 overflow-x-hidden">
      <div className="flex min-h-screen">
        {/* Sidebar */}
        <div className="hidden lg:block w-64 h-full">
          <AdminSidebar />
        </div>
        <Drawer
          height="full"
          title={"Admin"}
          isOpen={showSidebar}
          onClose={onClose}
        >
          <div className=" lg:hidden  w-full h-full">
            <AdminSidebar />
          </div>
        </Drawer>
        {/* Main Content */}
        <div className="flex-1 flex flex-col h-full overflow-y-auto bg-gray-800">
          {/* Header */}
          <div className="px-4 lg:px-8 py-6 border-b border-gray-700 ">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 mb-3 md:mb-0">
                  {activeTab === "knowledgebase" ? (
                    <Database className="text-blue-400" size={24} />
                  ) : (
                    <FileText className="text-purple-400" size={24} />
                  )}
                  <h1 className="text-2xl font-semibold text-white">
                    {activeTab === "knowledgebase"
                      ? "Knowledge Base Listing"
                      : "Prompt Template Listing"}
                  </h1>
                </div>
                <div
                  onClick={() => {
                    setShowSidebar(true);
                  }}
                  className="max-w-max lg:hidden"
                >
                  <Menu size={30} color="white" />
                </div>
              </div>
              <div className="flex gap-4 flex-col lg:flex-row items-start lg:items-center">
                <div className="bg-gray-700 border w-full lg:w-auto border-gray-600 rounded-lg px-4 py-2 flex  items-center gap-2">
                  <span className="font-semibold text-white text-sm">
                    {activeTab === "knowledgebase"
                      ? indexes.length
                      : promptTemplates.length}
                  </span>
                  <span className="text-gray-300  text-sm">
                    {activeTab === "knowledgebase"
                      ? "Knowledge base"
                      : "Templates"}
                  </span>
                </div>
                <div className="relative w-full lg:w-auto">
                  <input
                    type="text"
                    className={`pl-10 pr-4 py-2 w-full rounded-lg border border-gray-600 bg-gray-700 text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 ${
                      activeTab === "knowledgebase"
                        ? "focus:ring-blue-500"
                        : "focus:ring-purple-500"
                    } focus:border-transparent`}
                    placeholder={`Search ${
                      activeTab === "knowledgebase"
                        ? "knowledge bases"
                        : "templates"
                    }...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Search
                    className="absolute left-3 top-2.5 text-gray-400"
                    size={16}
                  />
                </div>
              </div>
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-1 mt-4">
              <button
                onClick={() => setActiveTab("knowledgebase")}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  activeTab === "knowledgebase"
                    ? "bg-blue-500 text-white"
                    : "text-gray-400 hover:text-white hover:bg-gray-700"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Database size={16} />
                  Knowledge Bases
                </div>
              </button>
              <button
                onClick={() => setActiveTab("prompttemplates")}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  activeTab === "prompttemplates"
                    ? "bg-purple-500 text-white"
                    : "text-gray-400 hover:text-white hover:bg-gray-700"
                }`}
              >
                <div className="flex items-center gap-2">
                  <FileText size={16} />
                  Prompt Templates
                </div>
              </button>
            </div>
          </div>

          {/* Table Content */}
          <div className="flex-1 p-3 lg:p-8">
            <div className="bg-gray-900 border border-gray-700 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-700">
                  <thead className="bg-gray-800">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                        {activeTab === "knowledgebase"
                          ? "Knowledge Base Name"
                          : "Template Name"}
                      </th>
                      {activeTab === "prompttemplates" && (
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          Type
                        </th>
                      )}
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                        {activeTab === "knowledgebase"
                          ? "Used in"
                          : "Knowledge Base"}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                        Status
                      </th>
                      {activeTab === "prompttemplates" && (
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          Created
                        </th>
                      )}
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-gray-900 divide-y divide-gray-700">
                    {loading ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-12 text-center">
                          <div className="flex items-center justify-center">
                            <div
                              className={`animate-spin rounded-full h-8 w-8 border-b-2 ${
                                activeTab === "knowledgebase"
                                  ? "border-blue-400"
                                  : "border-purple-400"
                              }`}
                            ></div>
                            <span className="ml-2 text-gray-400">
                              Loading{" "}
                              {activeTab === "knowledgebase"
                                ? "knowledge bases"
                                : "prompt templates"}
                              ...
                            </span>
                          </div>
                        </td>
                      </tr>
                    ) : currentData.length === 0 ? (
                      <tr>
                        <td
                          colSpan="6"
                          className="px-6 py-12 text-center text-gray-400"
                        >
                          {searchTerm
                            ? `No ${
                                activeTab === "knowledgebase"
                                  ? "knowledge bases"
                                  : "prompt templates"
                              } found matching your search`
                            : `No ${
                                activeTab === "knowledgebase"
                                  ? "knowledge bases"
                                  : "prompt templates"
                              } found`}
                        </td>
                      </tr>
                    ) : activeTab === "knowledgebase" ? (
                      filteredModels.map((model) => (
                        <tr
                          key={model._id}
                          className="hover:bg-gray-800 transition-colors"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div
                                className={`w-2 h-2 rounded-full ${getStatusDot(
                                  model.status || "active"
                                )} mr-3`}
                              ></div>
                              <span className="font-medium text-white text-sm">
                                {model.name}
                              </span>
                            </div>
                          </td>
                          <td
                            title={model.usedIn.join(", ")}
                            className="px-6 py-4 whitespace-nowrap text-gray-300 text-sm font-mono"
                          >
                            {model.usedIn.length || 0} bot
                            {model.usedIn.length <= 1 ? "" : "s"}
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-3 py-1 rounded-full text-xs capitalize font-medium border-0 bg-transparent ${getStatusColor(
                                model.status.toLowerCase() || "active"
                              )}`}
                            >
                              {model.status}
                            </span>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              {updatingModel === model._id ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400"></div>
                              ) : (
                                <>
                                  <button
                                    onClick={() => handleDeleteClick(model)}
                                    disabled={deleteLoading.includes(
                                      model.name
                                    )}
                                    className="text-red-400 hover:text-red-300 transition-colors"
                                    title="Delete Bot"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      filteredPromptTemplates.map((template) => (
                        <tr
                          key={template.id || template.name}
                          className="hover:bg-gray-800 transition-colors"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div
                                className={`w-2 h-2 rounded-full ${getStatusDot(
                                  template.status || "active"
                                )} mr-3`}
                              ></div>
                              <div>
                                <span className="font-medium text-white text-sm">
                                  {template.name}
                                </span>
                                {template.description && (
                                  <p className="text-xs text-gray-400 mt-1 truncate max-w-xs">
                                    {template.description}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 py-1 rounded-full text-xs capitalize font-medium ${getTypeColor(
                                template.type
                              )}`}
                            >
                              {template.type || "unknown"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-300 text-sm">
                            {template.knowledgeBase || "N/A"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-3 py-1 rounded-full text-xs capitalize font-medium ${getStatusColor(
                                template.status || "active"
                              )}`}
                            >
                              {template.status || "active"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-300 text-sm">
                            {template.createdAt
                              ? formatDate(template.createdAt)
                              : "N/A"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              {updatingModel === template.id ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-400"></div>
                              ) : (
                                <>
                                  <button
                                    onClick={() => handleDeleteClick(template)}
                                    disabled={deleteLoading.includes(
                                      template.id
                                    )}
                                    className="text-red-400 hover:text-red-300 transition-colors"
                                    title="Delete Template"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        disabled={deleteLoading.includes(
          modelToDelete?.name || modelToDelete?.id
        )}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDelete}
        title={`Delete ${
          activeTab === "knowledgebase" ? "Knowledge Base" : "Prompt Template"
        }`}
        loadingText="Deleting"
        message={`Are you sure you want to delete "${modelToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isDestructive={true}
      />
    </div>
  );
};

export default KnowledgeBaseListing;
